// Mongoose types removed - not needed in frontend types

// Core Permission Types
export enum SystemRole {
  SUPER_ADMIN = "super_admin",
  OWNER = "owner",
  ADMIN = "admin",
  MEMBER = "member",
  ORGANIZATION_ADMIN = "organization_admin",
  TEAM_LEAD = "team_lead",
  DESIGNER = "designer",
  VIEWER = "viewer",
  GUEST = "guest",
}

export enum ProjectRole {
  OWNER = "owner",
  ADMIN = "admin",
  EDITOR = "editor",
  COLLABORATOR = "collaborator",
  REVIEWER = "reviewer",
  VIEWER = "viewer",
}

export enum ComponentRole {
  CREATOR = "creator",
  MAINTAINER = "maintainer",
  CONTRIBUTOR = "contributor",
  CONSUMER = "consumer",
}

// Permission Actions
export enum PermissionAction {
  // Basic CRUD
  CREATE = "create",
  READ = "read",
  UPDATE = "update",
  DELETE = "delete",

  // Component-specific
  CLONE = "clone",
  MOVE = "move",
  RESIZE = "resize",
  STYLE = "style",

  // Content editing
  EDIT_CONTENT = "edit_content",
  EDIT_LAYOUT = "edit_layout",
  EDIT_BEHAVIOR = "edit_behavior",

  // Collaboration
  COMMENT = "comment",
  ANNOTATE = "annotate",
  SHARE = "share",

  // Advanced
  EXPORT = "export",
  IMPORT = "import",
  PUBLISH = "publish",
  ARCHIVE = "archive",
  RESTORE = "restore",
  VERSION = "version",

  // Management permissions
  MANAGE_PERMISSIONS = "manage_permissions",
  MANAGE_MEMBERS = "manage_members",
  MANAGE_SETTINGS = "manage_settings",
  BULK_OPERATIONS = "bulk_operations",
  EXECUTE_WORKFLOW = "execute_workflow",
  VIEW_ANALYTICS = "view_analytics",
}

// Component Permission Types (specific to component operations)
export enum ComponentPermissionType {
  CREATE = "create",
  READ = "read",
  UPDATE = "update",
  DELETE = "delete",
  CLONE = "clone",
  MOVE = "move",
  RESIZE = "resize",
  STYLE = "style",
  EDIT_CONTENT = "edit_content",
  EDIT_LAYOUT = "edit_layout",
  EDIT_BEHAVIOR = "edit_behavior",
  COMMENT = "comment",
  ANNOTATE = "annotate",
  SHARE = "share",
  EXPORT = "export",
  PUBLISH = "publish",
  VERSION = "version",
}

// Resource Types
export enum ResourceType {
  SYSTEM = "system",
  ORGANIZATION = "organization",
  TEAM = "team",
  PROJECT = "project",
  COMPONENT = "component",
  ASSET = "asset",
  COMMENT = "comment",
  USER = "user",
}

// Permission Context
export interface AttributeContext {
  user: {
    id: string;
    organizationId: string;
    teamIds: string[];
    location?: GeoLocation;
    timeZone: string;
    securityClearance: number;
    lastActivity: Date;
  };
  resource: {
    id: string;
    type: ResourceType;
    ownerId: string;
    organizationId: string;
    teamId?: string;
    sensitivity: SensitivityLevel;
    createdAt: Date;
    lastModified: Date;
  };
  environment: {
    timestamp: Date;
    ipAddress: string;
    deviceType: DeviceType;
    location?: GeoLocation;
    networkType: NetworkType;
    trustScore: number;
  };
  operation: {
    action: PermissionAction;
    scope: OperationScope;
    urgency: UrgencyLevel;
    context: string[];
  };
}

export interface GeoLocation {
  latitude: number;
  longitude: number;
  accuracy: number;
}

export enum SensitivityLevel {
  PUBLIC = "public",
  INTERNAL = "internal",
  CONFIDENTIAL = "confidential",
  RESTRICTED = "restricted",
}

export enum DeviceType {
  DESKTOP = "desktop",
  TABLET = "tablet",
  MOBILE = "mobile",
  UNKNOWN = "unknown",
}

export enum NetworkType {
  TRUSTED = "trusted",
  CORPORATE = "corporate",
  PUBLIC = "public",
  VPN = "vpn",
}

export enum OperationScope {
  SINGLE = "single",
  BULK = "bulk",
  SYSTEM = "system",
}

export enum UrgencyLevel {
  LOW = "low",
  NORMAL = "normal",
  HIGH = "high",
  CRITICAL = "critical",
}

// Permission Results
export interface PermissionResult {
  allowed: boolean;
  reason?: string;
  conditions?: PermissionCondition[];
  obligations?: Obligation[];
  advice?: Advice[];
  ttl?: number;
  evaluatedAt: Date;
  policyVersion?: string;
  cached?: boolean;
}

export interface PermissionCondition {
  type: string;
  operator: string;
  value: unknown;
  weight: number;
}

export interface Obligation {
  id: string;
  type: string;
  description: string;
  parameters: Record<string, unknown>;
  fulfillmentRequired: boolean;
}

export interface Advice {
  id: string;
  type: string;
  message: string;
  severity: "info" | "warning" | "error";
}

export interface PermissionContext {
  checkPermission: (
    resource: Resource,
    action: PermissionAction,
    context?: Partial<AttributeContext>,
  ) => Promise<PermissionResult>;
  checkBulkPermissions: (
    checks: PermissionCheck[],
  ) => Promise<Record<string, PermissionResult>>;
  hasPermission: (resource: Resource, action: PermissionAction) => boolean;
  getPermissionReason: (
    resource: Resource,
    action: PermissionAction,
  ) => string | undefined;
  refreshPermissions: (resourceIds?: string[]) => Promise<void>;
  invalidateCache: (pattern?: string) => void;
}

export interface Resource {
  id: string;
  type: ResourceType;
  ownerId?: string;
  organizationId?: string;
  teamId?: string;
  projectId?: string;
  attributes?: Record<string, unknown>;
}

// Permission Matrix Types
export interface PermissionMatrix {
  [roleId: string]: {
    [operationId: string]: PermissionResult;
  };
}

export interface PermissionMatrixProps {
  scope: PermissionScope;
  roles: Role[];
  operations: PermissionOperation[];
  permissions: PermissionMatrix;
  onPermissionChange: (
    roleId: string,
    operationId: string,
    granted: boolean,
  ) => void;
  onBulkChange: (changes: BulkPermissionChange[]) => void;
  readonly?: boolean;
  conflictResolution?: ConflictResolutionMode;
}

export interface Role {
  id: string;
  name: string;
  type: SystemRole | ProjectRole | ComponentRole;
  description?: string;
  permissions: string[];
  inheritsFrom?: string[];
  organizationId?: string;
  teamId?: string;
}

export interface PermissionOperation {
  id: string;
  name: string;
  description: string;
  type: PermissionAction;
  resourceType: ResourceType;
  category: string;
  riskLevel: RiskLevel;
}

export interface BulkPermissionChange {
  roleId: string;
  operationId: string;
  granted: boolean;
  reason?: string;
}

export enum PermissionScope {
  SYSTEM = "system",
  ORGANIZATION = "organization",
  TEAM = "team",
  PROJECT = "project",
  COMPONENT = "component",
}

export enum ConflictResolutionMode {
  EXPLICIT_DENY_WINS = "explicit_deny_wins",
  EXPLICIT_ALLOW_WINS = "explicit_allow_wins",
  MOST_RESTRICTIVE = "most_restrictive",
  MOST_PERMISSIVE = "most_permissive",
  HIERARCHICAL_PRECEDENCE = "hierarchical_precedence",
}

export enum RiskLevel {
  LOW = "low",
  MEDIUM = "medium",
  HIGH = "high",
  CRITICAL = "critical",
}

// Organization & Team Types
export interface Organization {
  id: string;
  name: string;
  domain: string;
  type: OrganizationType;
  members: OrganizationMember[];
  teams: Team[];
  departments: Department[];
  permissions: OrganizationPermissions;
  settings: OrganizationSettings;
}

export enum TeamType {
  DESIGN = "design",
  DEVELOPMENT = "development",
  MARKETING = "marketing",
  PRODUCT = "product",
  OPERATIONS = "operations",
  SUPPORT = "support",
  CROSS_FUNCTIONAL = "cross_functional",
}

export interface Team {
  id: string;
  name: string;
  slug: string;
  description?: string;
  organizationId: string;
  departmentId?: string;
  avatar?: string;
  leaderId: string;
  deputyIds: string[];
  members: TeamMember[];
  projects: string[];
  type: TeamType;
  specializations: string[];
  skills: string[];
  permissions: TeamPermissions;
}

export interface Department {
  id: string;
  name: string;
  description: string;
  organizationId: string;
  parentId?: string;
  managerId: string;
  members: DepartmentMember[];
  permissions: DepartmentPermissions;
  budget?: BudgetConstraints;
}

export enum OrganizationType {
  ENTERPRISE = "enterprise",
  BUSINESS = "business",
  STARTUP = "startup",
  NONPROFIT = "nonprofit",
  EDUCATION = "education",
}

export interface OrganizationMember {
  userId: string;
  role: SystemRole;
  permissions: string[];
  joinedAt: Date;
  status: MemberStatus;
}

export interface TeamMember {
  userId: string;
  role: TeamRole;
  permissions: string[];
  joinedAt: Date;
  status: MemberStatus;
}

export interface DepartmentMember {
  userId: string;
  role: DepartmentRole;
  permissions: string[];
  joinedAt: Date;
  status: MemberStatus;
}

export enum TeamRole {
  LEAD = "lead",
  DEPUTY = "deputy",
  MEMBER = "member",
  CONTRIBUTOR = "contributor",
  OBSERVER = "observer",
}

export enum DepartmentRole {
  MANAGER = "manager",
  SUPERVISOR = "supervisor",
  TEAM_LEAD = "team_lead",
  MEMBER = "member",
}

export enum MemberStatus {
  ACTIVE = "active",
  INVITED = "invited",
  SUSPENDED = "suspended",
  REMOVED = "removed",
}

// Permissions by entity type
export interface OrganizationPermissions {
  admin: {
    manage_users: boolean;
    manage_teams: boolean;
    manage_departments: boolean;
    manage_settings: boolean;
    view_audit_logs: boolean;
    manage_billing: boolean;
  };
  projects: {
    create_projects: boolean;
    delete_projects: boolean;
    manage_project_settings: boolean;
    access_all_projects: boolean;
  };
  security: {
    manage_security_policies: boolean;
    view_security_logs: boolean;
    manage_api_keys: boolean;
  };
}

export interface TeamPermissions {
  project: {
    create: boolean;
    delete: boolean;
    archive: boolean;
    settings: boolean;
    invite_members: boolean;
    remove_members: boolean;
  };
  resources: {
    upload_assets: boolean;
    manage_components: boolean;
    access_libraries: boolean;
    export_projects: boolean;
  };
  collaboration: {
    real_time_editing: boolean;
    commenting: boolean;
    reviewing: boolean;
    approvals: boolean;
  };
  admin: {
    team_settings: boolean;
    permission_management: boolean;
    audit_access: boolean;
  };
}

export interface DepartmentPermissions {
  budget: {
    view_budget: boolean;
    approve_expenses: boolean;
    manage_budget: boolean;
  };
  personnel: {
    hire_staff: boolean;
    manage_performance: boolean;
    access_hr_data: boolean;
  };
  operations: {
    manage_workflows: boolean;
    access_reports: boolean;
    manage_tools: boolean;
  };
}

export interface OrganizationSettings {
  security: {
    requireMFA: boolean;
    allowedDomains: string[];
    sessionTimeout: number;
    passwordPolicy: PasswordPolicy;
  };
  collaboration: {
    defaultPermissions: string[];
    allowExternalSharing: boolean;
    retentionPeriod: number;
  };
  compliance: {
    auditRetention: number;
    dataClassification: boolean;
    privacyControls: boolean;
  };
}

export interface PasswordPolicy {
  minLength: number;
  requireUppercase: boolean;
  requireLowercase: boolean;
  requireNumbers: boolean;
  requireSpecialChars: boolean;
  maxAge: number;
  preventReuse: number;
}

export interface BudgetConstraints {
  total: number;
  allocated: number;
  currency: string;
  period: "monthly" | "quarterly" | "yearly";
  approvalRequired: boolean;
  approvalLimit: number;
}

// Temporal Permission Types
export interface TemporalPermission {
  id: string;
  basePermission: PermissionResult;
  timeConstraints: TimeConstraint[];
  schedules: RecurringSchedule[];
  expiresAt?: Date;
  emergencyOverride?: EmergencyOverride;
}

export interface TimeConstraint {
  type: TimeConstraintType;
  value: TimeValue;
  timezone: string;
  exceptions?: TimeException[];
}

export enum TimeConstraintType {
  BUSINESS_HOURS = "business_hours",
  SPECIFIC_HOURS = "specific_hours",
  WEEKDAYS_ONLY = "weekdays_only",
  DATE_RANGE = "date_range",
  BLACKOUT_PERIODS = "blackout_periods",
}

export interface TimeValue {
  startHour?: number;
  endHour?: number;
  startDate?: Date;
  endDate?: Date;
  daysOfWeek?: number[];
  specificDates?: Date[];
}

export interface TimeException {
  date: Date;
  reason: string;
  override: boolean;
}

export interface RecurringSchedule {
  pattern: CronPattern;
  duration: number; // minutes
  permissions: PermissionAction[];
  priority: number;
}

export interface CronPattern {
  minute: string;
  hour: string;
  dayOfMonth: string;
  month: string;
  dayOfWeek: string;
}

export interface EmergencyOverride {
  enabled: boolean;
  authorizedUsers: string[];
  requiresApproval: boolean;
  auditRequired: boolean;
  maxDuration: number; // minutes
}

// Audit Types
export interface PermissionAuditEvent {
  id: string;
  timestamp: Date;
  userId: string;
  action: PermissionAction;
  resource: Resource;
  result: PermissionResult;
  context: AttributeContext;
  riskScore: number;
  metadata: Record<string, unknown>;
}

export interface AuditFilter {
  userId?: string;
  resourceType?: ResourceType;
  action?: PermissionAction;
  result?: "allowed" | "denied";
  dateRange?: {
    start: Date;
    end: Date;
  };
  riskLevel?: RiskLevel;
}

// Component-Level Permission Types
export interface ComponentPermission {
  componentId: string;
  permissions: {
    [action in PermissionAction]?: {
      allowed: boolean;
      conditions?: PermissionCondition[];
      inherited?: boolean;
      source: PermissionSource;
    };
  };
  propertyLevelPermissions: Record<string, PropertyPermission>;
  elementLevelPermissions: Record<string, ElementPermission>;
}

export interface PropertyPermission {
  property: string;
  read: boolean;
  write: boolean;
  conditions?: PermissionCondition[];
  validation?: ValidationRule[];
}

export interface ElementPermission {
  elementId: string;
  visible: boolean;
  interactive: boolean;
  editable: boolean;
  conditions?: PermissionCondition[];
}

export interface ValidationRule {
  type: string;
  parameters: Record<string, unknown>;
  message: string;
}

export enum PermissionSource {
  DIRECT = "direct",
  INHERITED = "inherited",
  ROLE = "role",
  POLICY = "policy",
  SYSTEM = "system",
}

// Additional types needed by PermissionContext
export interface PermissionRule {
  allowed: boolean;
  allowedRoles: (SystemRole | ProjectRole | ComponentRole)[];
  allowedUsers: string[];
  deniedUsers: string[];
  conditions: PermissionCondition[];
  inherited: boolean;
}

export interface ComponentPermissionSet {
  [key: string]: PermissionRule;
}

export interface Permission {
  id: string;
  action: PermissionAction;
  resource: ResourceType;
  scope: PermissionScope;
  granted: boolean;
  conditions?: PermissionCondition[];
  grantedAt: Date;
  grantedBy: string;
  expiresAt?: Date;
}

export interface RoleDefinition {
  id: string;
  name: string;
  description: string;
  permissions: Permission[];
  inheritsFrom?: string[];
  systemRole?: SystemRole;
  projectRole?: ProjectRole;
  componentRole?: ComponentRole;
}

export interface RoleAssignment {
  id: string;
  userId: string;
  roleId: string;
  resourceId?: string;
  resourceType?: ResourceType;
  assignedBy: string;
  assignedAt: Date;
  expiresAt?: Date;
  conditions?: PermissionCondition[];
}

export interface OrganizationMembership {
  id: string;
  userId: string;
  organizationId: string;
  role: SystemRole;
  status: MemberStatus;
  joinedAt: Date;
  invitedBy?: string;
  permissions: string[];
}

// Updated PermissionState interface
export interface PermissionState {
  userPermissions: Permission[];
  effectivePermissions: Permission[];
  systemRole: SystemRole;
  projectRoles: Map<string, ProjectRole>;
  componentRoles: Map<string, ComponentRole>;
  customRoles: RoleDefinition[];
  organization?: Organization;
  departments: Department[];
  teams: Team[];
  memberships: OrganizationMembership[];
  temporalPermissions: TemporalPermission[];
  lastFetched: Date;
  isLoading: boolean;
  error?: string;
}

// Updated PermissionCheck interface
export interface PermissionCheck {
  resource: Resource;
  action: PermissionAction;
  userId?: string;
  context?: Partial<AttributeContext>;
}

// Bulk permission checking
export interface BulkPermissionCheck {
  checks: PermissionCheck[];
  userId?: string;
  context?: Partial<AttributeContext>;
}

export interface BulkPermissionResult {
  results: Record<string, PermissionResult>;
  errors?: Record<string, string>;
  metadata?: {
    totalChecks: number;
    successCount: number;
    errorCount: number;
    processingTime: number;
  };
}

// Permission context value interface
export interface PermissionContextValue extends PermissionState {
  checkPermission: (check: PermissionCheck) => Promise<PermissionResult>;
  checkBulkPermissions: (
    checks: BulkPermissionCheck,
  ) => Promise<BulkPermissionResult>;
  refreshPermissions: () => Promise<void>;
  assignRole: (assignment: RoleAssignment) => Promise<void>;
  revokeRole: (roleId: string, userId: string) => Promise<void>;
  getComponentPermissions: (
    componentId: string,
    projectContext?: {
      ownerId?: string | { toString(): string };
      owner_id?: string | { toString(): string };
      members?: Array<{ userId: string | { toString(): string }; role: string }>;
    },
  ) => ComponentPermissionSet | undefined;
  updateComponentPermissions: (
    componentId: string,
    permissions: Partial<ComponentPermissionSet>,
  ) => Promise<void>;
  setOrganization: (organizationId: string) => Promise<void>;
  joinTeam: (teamId: string) => Promise<void>;
  leaveTeam: (teamId: string) => Promise<void>;
}

// Permission Matrix specific types
export interface PermissionMatrixCell {
  roleId: string;
  operationId: string;
  allowed: boolean;
  source: PermissionSource;
  inherited: boolean;
  conditions?: PermissionCondition[];
  conflicts?: PermissionMatrixConflict[];
}

export interface PermissionMatrixRow {
  roleId: string;
  roleName: string;
  roleType: SystemRole | ProjectRole | ComponentRole;
  cells: PermissionMatrixCell[];
  userCount: number;
  isExpanded?: boolean;
}

export interface PermissionMatrixFilter {
  showInherited: boolean;
  showOverrides: boolean;
  roleTypes?: (SystemRole | ProjectRole | ComponentRole)[];
  operationTypes?: PermissionAction[];
  searchQuery?: string;
  conflictsOnly?: boolean;
}

export interface PermissionMatrixConflict {
  type: "role_conflict" | "inheritance_conflict" | "condition_conflict";
  description: string;
  severity: "low" | "medium" | "high";
  suggestedResolution?: string;
}

// Viewer-specific permission types
export enum ViewerMode {
  READ_ONLY = "read_only",
  INTERACTIVE = "interactive",
  COLLABORATIVE = "collaborative",
  PRESENTATION = "presentation",
}

export interface ViewerPermissionState {
  mode: ViewerMode;
  canComment: boolean;
  canAnnotate: boolean;
  canRequestEdit: boolean;
  canEscalateToEditor: boolean;
  canExport: boolean;
  canShare: boolean;
  canViewSource: boolean;
  canViewHistory: boolean;
  restrictedFeatures: string[];
  escalationRequirements: EscalationRequirement[];
}

export interface EscalationRequirement {
  type: "approval" | "timeout" | "role_change" | "invitation";
  description: string;
  required: boolean;
  metadata?: Record<string, unknown>;
}

export interface ViewerEscalationRequest {
  id: string;
  userId: string;
  projectId: string;
  requestedRole: ProjectRole;
  requestedPermissions: PermissionAction[];
  reason?: string;
  status: "pending" | "approved" | "rejected" | "expired";
  createdAt: Date;
  expiresAt?: Date;
  approvedBy?: string;
  approvedAt?: Date;
  rejectedBy?: string;
  rejectedAt?: Date;
  rejectionReason?: string;
}

export interface ViewerPermissionCache {
  permissions: Record<string, PermissionResult>;
  escalationStatus: Record<string, ViewerEscalationRequest>;
  lastUpdated: Date;
  expiresAt: Date;
  optimizedForViewer: boolean;
}

export interface ViewerPermissionConfig {
  enableEscalation: boolean;
  defaultViewerMode: ViewerMode;
  escalationTimeout: number; // milliseconds
  requireApprovalFor: PermissionAction[];
  autoGrantFor: PermissionAction[];
  cacheOptimizations: {
    preloadCommonPermissions: boolean;
    bulkCheckInterval: number;
    backgroundRefresh: boolean;
  };
  roleBasedExperience: {
    [key in SystemRole]?: {
      allowedModes: ViewerMode[];
      defaultMode: ViewerMode;
      features: string[];
      restrictions: string[];
    };
  };
}
